/**
 * The "Select on Map" row context-menu — the shared affordance the
 * Platforms / Detections / Alerts list panels wrap each entity row with
 * (the 2026-06-09 two-slot selection-sync redesign, Phase 2).
 *
 * # What it does
 *
 * Right-clicking (or the context-menu key / ``Shift+F10`` on the focused
 * row — Radix gives both) opens a one-item menu, **"Select on Map"**, that
 * pushes the row's entity to the map:
 *
 *   1. :func:`selectOnMap` sets BOTH selection slots — ``mapSelected`` (so
 *      the map rings it + the row highlights) and ``inspected`` (so Entity
 *      Details shows it).
 *   2. :func:`requestMapFocus` raises the force-recenter signal so the map
 *      camera ``easeTo``s the entity **always — even if it's already in the
 *      viewport** (architect ruling 2026-06-09: "Ring + recenter + focus").
 *      This is the key difference from a map-glyph click, which stays
 *      pan-only-if-not-in-view (a glyph click never raises this signal).
 *
 * A plain left-click on the row is untouched — it still calls the panel's
 * own ``inspect`` handler (``inspected`` only; the map ring stays put). The
 * trigger renders the row ``asChild`` so the row keeps its own role /
 * tabindex / click + key handlers.
 *
 * # Unlocatable rows
 *
 * Two cases have nothing to recenter to, so the menu item is **disabled**
 * with a "No map location" hint rather than offering a no-op:
 *
 *   - **Aggregator** rows — aggregators are map-invisible by design (no
 *     marker, ever; the 2026-05-27 PR 5 §6 decision).
 *   - **Unlocated** detections / alerts — a null position (REQ-INGEST-044 /
 *     REQ-UI-069); the entity is list-only.
 *
 * The caller passes ``locatable={false}`` for these. A disabled item still
 * renders (so the affordance is discoverable + the operator learns *why*
 * it's unavailable) but cannot be activated by pointer or keyboard, and
 * never sets a slot or raises the focus signal.
 *
 * # Why a shared component
 *
 * One menu, three call sites — the Platforms, Detections, and Alerts rows
 * each wrap their ``<li>`` with this. Centralising the ``selectOnMap`` +
 * ``requestMapFocus`` wiring (and the disabled-hint behaviour) keeps the
 * three panels from drifting and matches the "reuse before rebuild"
 * discipline.
 *
 * Cross-references:
 *   - REQ-UI-051 (synchronized selection); REQ-UI-059 (keyboard / context-
 *     menu key); REQ-UI-096 (cross-pane "Select on Map"); REQ-UI-028
 *     (shadcn/ui layer).
 *   - REQ-INGEST-044 / REQ-UI-069 (nullable position — unlocated rows).
 *   - ADR-024 §5 (selection bus — 200 ms ease, force-recenter on push).
 *   - selection/selection-store.ts (``selectOnMap``) + map-focus-store.ts
 *     (``requestMapFocus``).
 */
import * as React from "react";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { useSelectionActions, type EntitySelection } from "@/selection/selection-store";
import { useRequestMapFocus } from "@/selection/map-focus-store";

/** Props for {@link SelectOnMapContextMenu}. */
export interface SelectOnMapContextMenuProps {
  /**
   * The entity the row represents. On activation this is written to both
   * selection slots (and, when ``locatable``, raised as the force-recenter
   * target).
   */
  readonly value: EntitySelection;
  /**
   * Whether the entity has a map location to recenter to. ``false`` (an
   * aggregator, or a null-position detection / alert) disables the menu
   * item with a "No map location" hint — no slot is set, no recenter
   * fires. Defaults to ``true``.
   */
  readonly locatable?: boolean;
  /**
   * The row element. Rendered ``asChild`` of the Radix trigger so the row
   * keeps its own role / tabindex / handlers — must be a single element
   * that forwards a ref + props (a plain ``<li>`` does).
   */
  readonly children: React.ReactNode;
}

/**
 * Wrap a list row with the "Select on Map" context menu.
 *
 * @param props - The row's {@link EntitySelection}, the ``locatable`` flag,
 *     and the row element as ``children``.
 */
export function SelectOnMapContextMenu({
  value,
  locatable = true,
  children,
}: SelectOnMapContextMenuProps): React.ReactElement {
  const { selectOnMap } = useSelectionActions();
  const requestMapFocus = useRequestMapFocus();

  const handleSelectOnMap = React.useCallback(() => {
    // Guard in addition to the disabled item — defence in depth so an
    // unlocatable entity can never set a slot or recenter even if the
    // disabled state were bypassed.
    if (!locatable) {
      return;
    }
    // Both slots: map ring + row highlight (mapSelected) AND Entity Details
    // (inspected). Then the force-recenter signal — the camera eases to the
    // entity regardless of the in-viewport check (the list-push divergence
    // from a glyph click).
    selectOnMap(value);
    requestMapFocus(value);
  }, [locatable, selectOnMap, requestMapFocus, value]);

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent data-testid="select-on-map-menu">
        <ContextMenuItem
          disabled={!locatable}
          onSelect={handleSelectOnMap}
          data-testid="select-on-map-item"
        >
          Select on Map
          {!locatable ? (
            <span className="ml-2 text-text-muted" data-testid="select-on-map-hint">
              No map location
            </span>
          ) : null}
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}
